// Admin review queue for Gemini meeting notes that did not file themselves.
// A row here is a meeting we received but would not attach to an athlete on a
// guess: unmatched, ambiguous, or untrusted. Matching is by the guest emails
// on the meeting's Calendar invite only (athlete, then guardian), never by
// names or the title.
//
// The athlete is picked from a dropdown of the concierge roster (GET
// /concierge/board), never typed. Filing writes a family's mental-health and
// recruiting notes onto a child's permanent record and there is no undo, so a
// free-text integer field was the wrong control: one mistyped digit filed onto
// a different child — or, before the server was scoped, onto a coach.
//
// An UNTRUSTED row offers NO controls at all -- neither assign nor ignore.
// An untrusted message can never be filed (the server enforces that off a
// persisted `trusted` flag, with a 409) and the UI must not invite the
// attempt; Ignore is withheld too because it rewrites `status`, which is
// exactly what used to erase the visible evidence that a message failed
// authentication. Such a row stays on screen as the record that something
// arrived and was rejected.
//
// That decision reads `note.trusted`, never `status === 'quarantined'`. The
// server stopped trusting `status` for exactly this — any writer can move it,
// and Ignore does — so the UI must not re-introduce the same inference from
// the other side. The assign endpoint can also 409 for a
// note that is already filed (re-filing would orphan a second document); that
// is not retryable either, so on any failure the row simply stays put and the
// reason is shown -- there is no optimistic removal to undo.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import AdminPage from '../components/AdminShell';
import '../components/Layout.css';
import './ConciergePage.css';

const WAITING = ['unmatched', 'ambiguous', 'quarantined'];

const REASON_LABEL = {
  none: 'No guest on the invite is a concierge athlete or guardian email',
  no_invite: 'No calendar invite on file for this meeting, so there are no guest emails to match',
  ambiguous: 'Guest emails matched more than one athlete — not filed, on purpose',
};

// The one place the untrusted decision is made, from the server's persisted
// fact. `status` is deliberately not consulted.
function isUntrusted(note) {
  return note.trusted === false;
}

function reasonText(note) {
  if (isUntrusted(note)) {
    return `Untrusted — ${note.match_reason || 'sender could not be verified'}`;
  }
  return REASON_LABEL[note.match_reason] || note.match_reason || 'Did not file';
}

export default function AdminMeetingNotesPage() {
  const { API_URL } = useAuth();
  const cfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const [notes, setNotes] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [picks, setPicks] = useState({});

  useEffect(() => {
    setLoading(true);
    axios.get(`${API_URL}/concierge/meeting-notes`, cfg())
      .then((res) => {
        const all = Array.isArray(res.data) ? res.data : [];
        setNotes(all.filter((n) => WAITING.includes(n.status)));
      })
      .catch((err) => setError(err.response?.data?.detail || 'Failed to load the queue.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [API_URL]);

  // The concierge roster, fetched once: it is exactly the set of athletes the
  // server will accept for a filing, so the picker offers nothing else.
  useEffect(() => {
    axios.get(`${API_URL}/concierge/board`, cfg())
      .then((res) => setAthletes(Array.isArray(res.data) ? res.data : []))
      .catch(() => setError('Could not load the concierge athlete list — '
        + 'reload before filing anything.'));
    // eslint-disable-next-line
  }, [API_URL]);

  const drop = (id) => setNotes((prev) => prev.filter((n) => n.id !== id));

  const assign = (note) => {
    const raw = picks[note.id];
    const athleteUserId = Number(raw);
    if (!athleteUserId) {
      setError('Choose the athlete to file this against.');
      return;
    }
    setError('');
    axios.post(`${API_URL}/concierge/meeting-notes/${note.id}/assign`,
      { athlete_user_id: athleteUserId }, cfg())
      .then(() => drop(note.id))
      .catch((err) => setError(err.response?.data?.detail || 'Failed to file the note.'));
  };

  const ignore = (note) => {
    setError('');
    axios.post(`${API_URL}/concierge/meeting-notes/${note.id}/ignore`, {}, cfg())
      .then(() => drop(note.id))
      .catch((err) => setError(err.response?.data?.detail || 'Failed to ignore the note.'));
  };

  return (
    <AdminPage
      active="meeting-notes"
      title="Meeting Note Exceptions"
      subtitle="Gemini notes that need an athlete · internal"
    >
      <div className="cb-wrap">
        {error && (
          <div className="cn-inline-msg cn-inline-msg--err" style={{ margin: '0 0 16px' }}>
            {error}
          </div>
        )}

        {loading ? (
          <div className="cn-loading">Loading…</div>
        ) : notes.length === 0 ? (
          <div className="cn-empty">Nothing waiting — every meeting filed itself.</div>
        ) : (
          <div className="cn-vtable">
            {notes.map((note) => (
              <div className="cn-visit" key={note.id}>
                <div className="cn-visit__college" style={{ marginBottom: 6 }}>
                  <span className="cn-ts-row__name">{note.meeting_title || '(untitled meeting)'}</span>
                </div>
                <div style={{ fontSize: 12, color: '#6B7C8E', marginBottom: 8 }}>
                  {note.meeting_date || '—'} · from {note.sender || 'unknown'} · {reasonText(note)}
                </div>
                {note.body_preview && (
                  <div style={{ fontSize: 13, color: '#07131F', marginBottom: 8 }}>
                    {note.body_preview}
                  </div>
                )}
                {!isUntrusted(note) && (
                  <div className="cn-visit__controls">
                    <select
                      className="cn-vinput"
                      style={{ maxWidth: 240 }}
                      aria-label={`Assign ${note.meeting_title} to athlete`}
                      value={picks[note.id] ?? ''}
                      onChange={(e) => setPicks((p) => ({ ...p, [note.id]: e.target.value }))}
                    >
                      <option value="">Choose an athlete…</option>
                      {athletes.map((a) => (
                        <option key={a.user_id} value={a.user_id}>{a.name}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="cn-btn-secondary"
                      aria-label={`File ${note.meeting_title}`}
                      onClick={() => assign(note)}
                    >File</button>
                    <button
                      type="button"
                      className="cn-vdelete"
                      aria-label={`Ignore ${note.meeting_title}`}
                      onClick={() => ignore(note)}
                    >Ignore</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminPage>
  );
}
